import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { useForm } from "react-hook-form";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  EditHealthReportFormValues,
  EditHealthReportSchema,
} from "../schema/health-report.schema";
import { HealthReport } from "src/redux/types/_customer.type";
import { toast } from "react-toastify";
import { useEditHealtReport } from "../hooks/useEditHealthReport";

const healthParameters = [
  {
    label: "BLOOD PRESSURE (mmHg) Systolic",
    name: "blood_pressure_systolic",
    type: "number",
  },
  {
    label: "BLOOD PRESSURE (mmHg) Diastolic",
    name: "blood_pressure_diastolic",
    type: "number",
  },
  {
    label: "PULSE RATE (BPM)",
    name: "pulse_rate",
    type: "number",
  },
  {
    label: "BLOOD SUGAR LEVEL (mg/dL) Random",
    name: "blood_sugar_random",
    type: "number",
  },
  {
    label: "BODY WEIGHT (Kg)",
    name: "body_weight",
    type: "number",
  },
  {
    label: "BMI",
    name: "bmi",
    type: "number",
  },
  {
    label: "BODY FAT (%)",
    name: "body_fat",
    type: "number",
  },
  {
    label: "MUSCLE RATE (%)",
    name: "muscle_rate",
    type: "number",
  },
  {
    label: "BODY WATER (%)",
    name: "body_water",
    type: "number",
  },
  {
    label: "BONE MASS (Kg)",
    name: "bone_mass",
    type: "number",
  },
  {
    label: "BMR (Kcal)",
    name: "bmr",
    type: "number",
  },
  {
    label: "METABOLIC AGE (Years)",
    name: "metabolic_age",
    type: "number",
  },
  {
    label: "VISCERAL FAT (%)",
    name: "visceral_fat",
    type: "number",
  },
  {
    label: "SUBCUTANEOUS FAT (%)",
    name: "subcutaneous_fat",
    type: "number",
  },
  {
    label: "PROTEIN MASS (Kg)",
    name: "protein_mass",
    type: "number",
  },
  {
    label: "MUSCLE MASS (Kg)",
    name: "muscle_mass",
    type: "number",
  },
  {
    label: "WEIGHT WITHOUT FAT (Kg)",
    name: "weight_witout_fat",
    type: "number",
  },
  {
    label: "OBESITY LEVEL",
    name: "obesity_level",
    type: "text",
  },
  {
    label: "SKELETAL MUSCLE MASS (Kg)",
    name: "skeletal_muscle_mass",
    type: "number",
  },
  {
    label: "IDEAL BODY WEIGHT (Kg)",
    name: "ideal_body_weight",
    type: "number",
  },
  {
    label: "WATER WEIGHT (Kg)",
    name: "water_weight",
    type: "number",
  },
  {
    label: "OBESITY GRADE",
    name: "obesity_grade",
    type: "text",
  },
  {
    label: "FAT MASS (Kg)",
    name: "fat_mass",
    type: "number",
  },
  {
    label: "BODY SCORE",
    name: "body_score",
    type: "number",
  },
];

const activity = [
  "Sedentary (Sitting, office work)",
  "Lightly Active (Light exercise/sports 1-3 days/week)",
  "Moderately Active (Moderate exercise/sports 3-5 days/week)",
  "Very Active (Hard exercise/sports 6-7 days/week)",
  "Extra Active (Very hard exercise or physical job + training)",
];

const EditHealthReportForm = ({
  report,
  memberId,
  close,
}: {
  report: HealthReport;
  memberId: number;
  close: () => void;
}) => {
  const form = useForm<EditHealthReportFormValues>({
    resolver: zodResolver(EditHealthReportSchema),
    defaultValues: report,
  });

  const { mutateAsync: editReport, isPending, isError } = useEditHealtReport();

  const onSubmit = async (data: EditHealthReportFormValues) => {
    if (!report.id) {
      toast.error("Select a Report to Edit");
    }

    await editReport(
      {
        reportId: report.id,
        values: data,
        memberId,
      },
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  if (isError) {
    toast.error("Failed to Update Report");
  }

  return (
    <div className="bg-card rounded-2xl border border-gray-100 mx-auto">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6 w-full"
        >
          <div className="w-full rounded-lg">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {/* Header */}
                <tr className="bg-gradient-to-r from-blue-100 to-blue-50">
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-[#42679e]">
                    Health Parameters
                  </th>
                  <th
                    className="border border-gray-300 px-3 py-2 text-right font-medium text-[#42679e]"
                    colSpan={3}
                  >
                    Date:&nbsp;
                    <span className="font-medium text-[#42679e]">
                      {moment(new Date()).format("MMM Do YY")}
                    </span>
                  </th>
                </tr>

                {/* General Parameters */}
                {healthParameters.map((param, i) => (
                  <tr
                    key={i}
                    className={`${
                      i % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-blue-50 transition`}
                  >
                    <td className="border border-gray-300 px-3 py-2 text-[#42679e] font-medium">
                      {param.label}
                    </td>
                    <td
                      className="border border-gray-300 px-3 py-2"
                      colSpan={3}
                    >
                      <FormField
                        control={form.control}
                        name={param.name as keyof EditHealthReportFormValues}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                {...field}
                                type={param.type}
                                min={param.type === "number" ? 0 : undefined}
                                value={field.value ?? ""}
                                step={
                                  param.type === "number" ? "any" : undefined
                                }
                                className="w-full text-center text-sm rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-400 transition text-[#42679e] font-medium px-2 py-2 h-9 box-border"
                                placeholder="Enter value"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </td>
                  </tr>
                ))}

                {/* ABNORMAL MEDICAL REPORTS SECTION */}
                <tr>
                  <td
                    colSpan={4}
                    className="bg-gradient-to-r from-blue-200 to-blue-100 text-center text-sm font-medium text-[#42679e] py-2"
                  >
                    Other Parameters
                  </td>
                </tr>

                <tr className="bg-white pt-3">
                  <td
                    className="border border-gray-300 px-2 py-1 text-center font-medium"
                    colSpan={4}
                  >
                    <div className="h-fit grid grid-cols-3 gap-6 text-xs text-[#42679e]">
                      {/* Medical History */}
                      <div className="">
                        <h4 className="text-center font-medium mb-1">
                          Abnormal Blood Reports (not older than 6 month)
                        </h4>
                        <FormField
                          control={form.control}
                          name="medical_history"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Enter blood reports"
                                  type="text"
                                  value={field.value ?? ""}
                                  className="border rounded-lg px-2 py-2 h-9 box-border"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Medication */}
                      <div className="">
                        <h4 className="text-center font-medium mb-1">
                          Medication
                        </h4>
                        <FormField
                          control={form.control}
                          name="medication"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder="Enter medication details"
                                  type="text"
                                  value={field.value ?? ""}
                                  className="border rounded-lg px-2 py-2 h-9 box-border"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Activity Level */}
                      <div className="">
                        <h4 className="text-center font-medium mb-1">
                          Activity Level
                        </h4>
                        <FormField
                          control={form.control}
                          name="activity_level"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <select
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                  className="border rounded-lg px-2 py-2 w-full h-9 box-border"
                                >
                                  <option value="">
                                    Select Activity level
                                  </option>
                                  {activity.map((activity, i) => (
                                    <option value={activity} key={i}>
                                      {activity}
                                    </option>
                                  ))}
                                </select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                {/* Submit Button */}
                <tr>
                  <td
                    className="border border-gray-300 px-3 py-3 text-center bg-blue-50"
                    colSpan={4}
                  >
                    <Button
                      type="submit"
                      disabled={isPending || form.formState.isSubmitting}
                    >
                      {isPending || form.formState.isSubmitting ? (
                        <span className="loading loading-dots loading-md"></span>
                      ) : (
                        "Save Report"
                      )}
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditHealthReportForm;
